/**
 * sendMobileNotification.ts — Envía un MobileNotificationPayload como notificación real del SO.
 *
 * QUÉ HACE: Toma un payload ya armado (título/cuerpo/categoría) y lo entrega al
 *           adaptador de plataforma (@/lib/platform), que decide si usa
 *           Capacitor Local Notifications (Android/iOS), Tauri (escritorio) o
 *           la Notification API del navegador (web).
 * QUÉ INVOCA: notifications de @/lib/platform — única puerta a Capacitor/Tauri/Web.
 * QUÉ EXPORTA: sendMobileNotification, requestMobileNotificationPermission
 * QUÉ NO DEBE HACER:
 *   - No debe lanzar errores: notificar es best-effort y nunca debe romper el
 *     flujo de negocio que la origina (ej. registrar una venta o una auditoría).
 *   - No debe decidir preferencias de usuario (eso vive en notificationPrefsStore).
 */
import { notifications } from "@/lib/platform";
import type { MobileNotificationPayload } from "./mobileNotificationDesign";

/** Solicita el permiso de notificaciones del sistema operativo. */
export async function requestMobileNotificationPermission(): Promise<boolean> {
  return notifications.requestPermission();
}

/** true si la plataforma actual puede mostrar notificaciones del sistema. */
export function isMobileNotificationSupported(): boolean {
  return notifications.isSupported();
}

/** Dispara la notificación (best-effort; nunca interrumpe al llamador si falla). */
export async function sendMobileNotification(
  payload: MobileNotificationPayload,
): Promise<void> {
  try {
    await notifications.send(payload.title, payload.body, {
      channelId: payload.channelId,
      smallIcon: payload.smallIcon,
      iconColor: payload.iconColor,
      largeIcon: payload.largeIcon,
      id: payload.id,
      data: payload.data,
      actionTypeId: payload.actionTypeId,
      actionLabel: payload.actionLabel,
      priority: payload.priority,
    });
  } catch {
    /* best-effort: nunca debe romper el flujo que originó la notificación */
  }
}
